import React, { useState } from 'react';
import { Alert, Form, Button, Card, FilterGrid } from '@/components/ui';
import reportService, { RockfallPreviewData } from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import Toast from '../../../components/Toast';
import { ServiceArea } from '../../../services/areasService';

interface RockfallReportViewProps {
  serviceAreas: ServiceArea[];
  canCreateRockfall: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const RockfallReportView: React.FC<RockfallReportViewProps> = ({ 
  serviceAreas, 
  canCreateRockfall, 
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    startDate: '',
    endDate: ''
  });
  const [previewData, setPreviewData] = useState<RockfallPreviewData[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => handleGeneratePreview(page, pageSize)
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleGeneratePreview = async (page: number = 1, pageSize?: number) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await reportService.getRockfallPreviewData({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: pageSize || pagination.limit,
        sortField,
        sortDirection
      });

      setPreviewData(response.data);
      setPagination({
        ...response.pagination,
        onPageChange: (page: number, pageSize?: number) => handleGeneratePreview(page, pageSize)
      });
      
    } catch (error) {
      setErrorMessage('Failed to generate rockfall report preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSort = (field: string) => {
    // Determine sort direction: if same field, toggle direction; if new field, default to desc
    const newDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    handleGeneratePreview(1); // Reset to first page when sorting
  };

  const handleExport = async (customFilename?: string) => {
    setLoadingExport(true);
    try {
      await reportService.generateRockfallReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename
      });

      showSuccessToast('Rockfall report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export rockfall report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Generate default filename based on format: SA{serviceArea}ROCK{YYYYMMDD}
  const generateDefaultFilename = () => {
    if (filters.serviceArea && filters.endDate) {
      const serviceAreaCode = filters.serviceArea.replace(/\D/g, '') || '00';
      const date = new Date(filters.endDate);
      const dateStr = `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`;
      return `SA${serviceAreaCode}ROCK${dateStr}`;
    }
    return 'rockfall_report';
  };

  const handleExportWithFilename = async (postedDate: string, customFilename?: string) => {
    setLoadingExport(true);
    try {
      await reportService.generateRockfallReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename
      });

      showSuccessToast('Rockfall report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export rockfall report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  const columns = [
    { 
      label: 'Record Type', 
      key: 'recordType',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'Service Area', 
      key: 'serviceArea',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Estimated Date', 
      key: 'estimatedRockfallDate',
      sortable: true,
      width: '150px'
    },
    { 
      label: 'Estimated Time', 
      key: 'estimatedRockfallTime',
      sortable: false,
      width: '140px'
    },
    { 
      label: 'Start Latitude', 
      key: 'startLatitude',
      sortable: false,
      width: '140px'
    },
    { 
      label: 'Start Longitude', 
      key: 'startLongitude',
      sortable: false,
      width: '140px'
    },
    { 
      label: 'End Latitude', 
      key: 'endLatitude',
      sortable: false,
      width: '140px'
    },
    { 
      label: 'End Longitude', 
      key: 'endLongitude',
      sortable: false,
      width: '140px'
    },
    { 
      label: 'Highway Unique', 
      key: 'highwayUnique',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Highway Name', 
      key: 'highwayUniqueName',
      sortable: false,
      width: '160px'
    },
    { 
      label: 'Location Description', 
      key: 'locationDescription',
      sortable: false,
      width: '200px'
    },
    { 
      label: 'Ditch Volume', 
      key: 'ditchVolume',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'Travelled Lanes Volume', 
      key: 'travelledLanesVolume',
      sortable: false,
      width: '180px'
    },
    { 
      label: 'Heavy Precipitation', 
      key: 'heavyPrecipitation',
      sortable: false,
      width: '160px'
    },
    { 
      label: 'Freeze/Thaw', 
      key: 'freezeThaw',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'Ditch Snow/Ice', 
      key: 'ditchSnowIce',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Vehicle Damage', 
      key: 'vehicleDamage',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Comments', 
      key: 'comments',
      sortable: false,
      width: '200px'
    },
    { 
      label: 'Name', 
      key: 'name',
      sortable: false,
      width: '160px'
    },
    { 
      label: 'Phone Number', 
      key: 'mcPhoneNumber',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Maintenance Contractor', 
      key: 'nameOfMaintenanceContractor',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Report Date', 
      key: 'reportDate',
      sortable: false,
      width: '130px'
    }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
      <ReportParametersCard
        onGenerate={() => handleGeneratePreview(1)}
        onExport={() => {}} // Remove export functionality from top
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true} // Disable export button in top bar
        generateButtonText="Run Report"
        hideExportButton={true} // Hide the export button entirely
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed} // Track collapse state
      >
        <FilterGrid>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            <Form.Select
              size="sm"
              value={filters.serviceArea}
              onChange={(e) => setFilters({ ...filters, serviceArea: e.target.value })}
              disabled={loadingServiceAreas}
            >
              <option value="">All Service Areas</option>
              {serviceAreas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Date Range</Form.Label>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                style={{ width: '140px' }}
              />
              <span className="tw-text-content-muted tw-text-sm">to</span>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                style={{ width: '140px' }}
              />
            </div>
          </Form.Group>
        </FilterGrid>
      </ReportParametersCard>
      </div>
      
      
      {/* Report Data Section - Flexible */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // Important for flex child
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
          <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
          {pagination.total > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowExportModal(true)}
              disabled={!canCreateRockfall}
            >
              <i className="bi bi-download tw-mr-1"></i>
              Export Report
            </Button>
          )}
        </Card.Header>

          {/* Table Container - ReportPreviewTable owns its own scroll region (single mechanism,
              see ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
          <ReportPreviewTable
            columns={columns}
            data={previewData}
            pagination={pagination}
            loading={loadingPreview}
            emptyMessage="No data available. Click 'Run Report' to load data."
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            isFiltersCollapsed={isFiltersCollapsed} // Pass collapse state to table
            yellowHeaderColumn="" // No yellow header column
          />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={handleExport}
        onFinalExport={handleExportWithFilename}
        loadingPreliminary={loadingExport}
        loadingFinal={loadingExport}
        isRockfall={true}
        showFilenameInput={true}
        defaultFilename={generateDefaultFilename()}
      />

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="success"
      />
    </div>
  );
};

export default RockfallReportView;